import { imageVariantLink } from '@/common/media';
import { formatRole } from '@/components/Widget/WhosOnBoardWidget/WhosOnBoardWidget.constants';
import { contactHref } from '@/utils/contact-href';
import { eventHref } from '@/utils/event-href';
import { taskHref, taskKind } from '@/utils/task-href';

import type { GlobalSearchResponse, SearchLocationDto } from './global-search.dto';

/**
 * One row in the palette: a record, where it lives, and where clicking it goes.
 */
export type SearchHit = {
  /** Unique across categories — ids repeat between services. */
  id: string;
  /** Group heading, e.g. "Tasks". */
  category: string;
  label: string;
  /** Second line: enough to tell two same-named records apart. */
  subtitle?: string;
  /**
   * Nav group key this category belongs to, matching `getVisibleNavGroups()`, so
   * the scope pill filters backend hits and page hits the same way. `undefined`
   * for categories with no section of their own — visible only in "All".
   */
  section?: string;
  to: string;
  /** Set on a person row: drawn with their avatar and the matched name marked. */
  person?: { firstName: string; lastName: string; avatar: null | string };
};

/** What a destination sometimes needs beyond the hit itself. */
export type HitContext = {
  vesselId?: null | string;
};

const dash = (...parts: (null | string | undefined)[]) => parts.filter(Boolean).join(' · ');

/**
 * A profile's location as one line.
 *
 * People's search endpoint hands back `job_profile.location` — a jsonb column
 * holding a `shared.Location` — as raw text, so putting it in a subtitle
 * unedited prints `{"address":"Monaco",…}`. Prefer the formatted address, which
 * is the field the crew screens show (Crew.tsx, CrewPreviewPanel.tsx), then the
 * place name, then the city; drop the hit's location entirely rather than show
 * a blob. Accepts the object too, for the day the backend types the field.
 */
const locationText = (location: null | SearchLocationDto | string | undefined) => {
  let loc: SearchLocationDto | undefined;

  if (typeof location === 'string') {
    const text = location.trim();
    if (!text) return undefined;
    // A plain place string is already what we want to print.
    if (!text.startsWith('{')) return text;
    try {
      loc = JSON.parse(text) as SearchLocationDto;
    } catch {
      return undefined;
    }
  } else {
    loc = location ?? undefined;
  }

  if (!loc) return undefined;

  return loc.address || loc.name || [loc.city, loc.state].filter(Boolean).join(', ') || undefined;
};

/**
 * Category → rows.
 *
 * Destinations use the record-opening route where the app already has one
 * (`/crew/:personId`, `/service-providers/:id`, `/inbox/:chatId`,
 * `/maintain/equipment/:categoryId/:itemId`, `?task-id=` on whichever list owns
 * the task — see `taskHref`; `?event=` on the Plan calendar — see `eventHref`).
 * Where it does not, the row lands on the list that owns the record rather than
 * inventing a deep link — `?focus=` was tried in this app and rejected.
 */
export const collectHits = (
  response: GlobalSearchResponse,
  { vesselId }: HitContext = {},
): SearchHit[] => {
  const hits: SearchHit[] = [];

  const push = (
    prefix: string,
    category: string,
    section: string | undefined,
    items: Omit<SearchHit, 'category' | 'section'>[],
  ) => {
    for (const item of items) {
      hits.push({ ...item, id: `${prefix}-${item.id}`, category, section });
    }
  };

  // ^ ---------------------- activity ----------------------

  const activity = response.activity;
  if (activity) {
    // Tasks and maintenance jobs come back from one endpoint but live on two
    // lists, split on `equipment` exactly as Tasks and the Schedule split their
    // own rows — so a hit is filed under the category whose page can show it,
    // and `taskHref` picks the destination. Service requests carry equipment
    // too and are not separable here (see SearchTaskDto.equipment), so they file
    // as maintenance, which is the list that holds them.
    const taskRow = (t: (typeof activity.tasks)[number]) => ({
      id: t.id,
      label: t.title,
      subtitle: dash(t.equipment?.name, t.status, t.priority, t.description),
      to: taskHref(t),
    });

    push(
      'task',
      'Tasks',
      'tasks',
      activity.tasks.filter((t) => taskKind(t) === 'task').map(taskRow),
    );
    push(
      'maintenance-task',
      'Maintenance',
      'maintain',
      activity.tasks.filter((t) => taskKind(t) === 'maintenance').map(taskRow),
    );
    push(
      'event',
      'Events',
      'plan',
      activity.events.map((e) => ({
        id: e.id,
        label: e.title,
        subtitle: dash(e.type, e.description),
        to: eventHref(e.id),
      })),
    );
    push(
      'image',
      'Gallery',
      'documents',
      activity.gallery_images.map((g) => ({
        id: g.id,
        label: g.name,
        subtitle: g.content_type,
        // The gallery is vessel-scoped; with no active vessel there is nothing
        // to open, so fall back to the file tree. Memories is what replaced the
        // old `<vesselId>.gallery` folder.
        to: vesselId ? '/documents/memories' : '/documents',
      })),
    );
  }

  // ^ ---------------------- vessel ----------------------

  const vessel = response.vessel;
  if (vessel) {
    push(
      'equipment',
      'Equipment',
      'maintain',
      vessel.equipment.map((e) => ({
        id: e.id,
        label: e.name,
        subtitle: dash(e.category_name, e.type_name),
        // The item page is nested under its category, so a hit without one can
        // only reach the category grid.
        to: e.category_id ? `/maintain/equipment/${e.category_id}/${e.id}` : '/maintain',
      })),
    );
    // A generator is the recurring rule, not one job — a separate heading from
    // "Maintenance" above, which holds the jobs it produces. The panel groups by
    // category name, so sharing one would merge two different kinds of record
    // under a heading that only describes half of them.
    push(
      'generator',
      'Maintenance schedules',
      'maintain',
      vessel.activity_generators.map((g) => ({
        id: g.id,
        label: g.title,
        subtitle: dash(g.maintenance_title, g.activity_type, g.status),
        to: '/maintain/schedule',
      })),
    );
    push(
      'vessel',
      'Vessels',
      undefined,
      vessel.vessels.map((v) => ({
        id: v.id,
        label: v.vessel_name,
        subtitle: dash(v.make, v.model),
        to: '/my-yachts',
      })),
    );
    push(
      'marina',
      'Marinas',
      'discover',
      vessel.marinas.map((m) => ({
        id: m.id,
        label: m.title,
        subtitle: m.type,
        to: '/discover/marinas',
      })),
    );
    push(
      'recipe',
      'Recipes',
      'plan',
      vessel.recipes.map((r) => ({
        id: r.id,
        label: r.name,
        subtitle: r.description ?? undefined,
        to: '/plan',
      })),
    );
    push(
      'menu',
      'Menus',
      'plan',
      vessel.menus.map((m) => ({
        id: m.id,
        label: m.meal_type,
        subtitle: m.date,
        to: `/event-menu/${m.event_id}`,
      })),
    );
    push(
      'menu-request',
      'Menu requests',
      'plan',
      vessel.menu_change_requests.map((r) => ({
        id: r.id,
        label: r.message,
        subtitle: r.status,
        to: `/event-menu/${r.event_id}`,
      })),
    );
    push(
      'chef-note',
      'Chef Notes',
      'plan',
      vessel.chef_notes.map((n) => ({ id: n.id, label: n.note, to: '/plan' })),
    );
    push(
      'daily-log',
      'Daily logs',
      'manage',
      vessel.daily_logs.map((l) => ({
        id: l.id,
        label: l.date,
        subtitle: dash(l.location, l.country),
        to: '/manage/logs/vessel-daily-log',
      })),
    );
    push(
      'nav-log',
      'Navigation logs',
      'manage',
      vessel.navigation_logs.map((l) => ({
        id: l.id,
        label: `${l.passage_from} → ${l.passage_to}`,
        subtitle: l.date,
        to: '/manage/logs/daily-navigation-log',
      })),
    );
    push(
      'oil-log',
      'Oil logs',
      'manage',
      vessel.oil_logs.map((l) => ({
        id: l.id,
        label: l.date,
        subtitle: l.responsible_name,
        to: '/manage/logs/oil-log',
      })),
    );
    push(
      'trash-log',
      'Trash logs',
      'manage',
      vessel.trash_logs.map((l) => ({
        id: l.id,
        label: l.date,
        subtitle: l.captain_name,
        to: '/manage/logs/trash-log',
      })),
    );
    push(
      'safety',
      'Safety instructions',
      'manage',
      vessel.safety_instructions.map((i) => ({
        id: i.id,
        label: i.instructions,
        to: '/manage/logs/safety-log',
      })),
    );
  }

  // ^ ---------------------- people ----------------------

  const people = response.people;
  if (people) {
    // Everyone on the active vessel's roster, whatever their role, opening on
    // their card in Messages' Contacts view (MGVN-21770) — whether or not they
    // are on the caller's own contact list.
    push(
      'person',
      'Contacts',
      'manage',
      people.people.map((p) => ({
        id: p.id,
        label: `${p.first_name} ${p.last_name}`.trim(),
        subtitle: p.role ? formatRole(p.role) : undefined,
        to: contactHref(p.id),
        person: {
          firstName: p.first_name,
          lastName: p.last_name,
          avatar: imageVariantLink(p.avatar, 28),
        },
      })),
    );
    push(
      'profile',
      'Crew profiles',
      'discover',
      people.profiles.map((p) => ({
        id: p.person_id,
        label: p.email,
        subtitle: dash(p.positions?.join(', '), locationText(p.location)),
        to: `/crew/${p.person_id}`,
      })),
    );
    push(
      'connection',
      'Connections',
      undefined,
      people.connections.map((c) => ({
        id: c.id,
        label: `${c.receiver_first_name} ${c.receiver_last_name}`.trim(),
        subtitle: c.status,
        to: `/crew/${c.receiver_id}`,
      })),
    );
    push(
      'crew-note',
      'Crew notes',
      'manage',
      people.notes.map((n) => ({
        id: n.person_id,
        label: n.text,
        subtitle: `${n.first_name} ${n.last_name}`.trim(),
        to: `/crew/${n.person_id}`,
      })),
    );
    push(
      'crew-doc',
      'Crew documents',
      'manage',
      people.documents.map((d) => ({
        id: d.id,
        label: d.name,
        subtitle: `${d.first_name} ${d.last_name}`.trim(),
        to: `/crew/${d.person_id}`,
      })),
    );
    push(
      'invite',
      'Invitations',
      'manage',
      people.invites.map((i) => ({
        id: i.id,
        label: i.name,
        subtitle: i.role,
        to: '/manage/crew',
      })),
    );
    // The caller's own contact list, opening on the same card as the vessel's
    // Contacts. Named apart from them: groups are keyed by their heading.
    push(
      'contact',
      'Chat contacts',
      'inbox',
      people.contacts.map((c) => ({
        id: c.id,
        label: c.name,
        subtitle: c.type === 'service_provider' ? 'Service provider' : 'Person',
        to: contactHref(c.contact_id),
      })),
    );
  }

  // ^ ---------------------- documents ----------------------

  const documents = response.documents;
  if (documents) {
    push(
      'document',
      'Library',
      'documents',
      documents.documents.map((d) => ({
        id: d.id,
        label: d.name,
        subtitle: d.path,
        // The file tree is addressed by folder, so a file opens the folder that
        // contains it; a root-level file opens the root.
        to: d.parent_id ? `/documents/${d.parent_id}` : '/documents',
      })),
    );
  }

  // ^ ---------------------- wallet ----------------------

  const wallet = response.wallet;
  if (wallet) {
    push(
      'transaction',
      'Expenses',
      'manage',
      wallet.transactions.map((t) => ({
        id: t.id,
        label: t.description ?? t.type,
        subtitle: dash(t.expenses_category_title, t.status),
        to: '/manage/transactions',
      })),
    );
    push(
      'invoice',
      'Invoices',
      'manage',
      wallet.invoices.map((i) => ({
        id: i.id,
        label: i.invoice_number ?? i.id,
        subtitle: dash(i.company_name, i.status),
        to: '/manage/invoices',
      })),
    );
    push(
      'quote',
      'Quotes',
      'manage',
      wallet.quotes.map((q) => ({
        id: q.id,
        label: q.quote_number ?? q.id,
        subtitle: q.status,
        to: '/quote',
      })),
    );
    push(
      'budget',
      'Budgets',
      'manage',
      wallet.budgets.map((b) => ({
        id: b.id,
        label: b.expenses_category,
        subtitle: String(b.year),
        to: '/manage/budget-details',
      })),
    );
    push(
      'category',
      'Expense categories',
      'manage',
      wallet.transaction_categories.map((c) => ({
        id: c.id,
        label: c.title,
        to: '/manage/transactions',
      })),
    );
  }

  // ^ ---------------------- service providers ----------------------

  const sp = response.serviceProviders;
  if (sp) {
    push(
      'provider',
      'Service Providers',
      'discover',
      sp.service_providers.map((p) => ({
        id: p.id,
        label: p.name,
        subtitle: dash(p.service_type, p.short_description),
        to: `/service-providers/${p.id}`,
      })),
    );
    push(
      'port',
      'Ports',
      'discover',
      sp.ports.map((p) => ({
        id: p.id,
        label: p.name,
        subtitle: dash(p.city, p.state),
        to: '/discover/marinas',
      })),
    );
  }

  // ^ ---------------------- chat ----------------------

  const chat = response.chat;
  if (chat) {
    push(
      'room',
      'Chats',
      'inbox',
      chat.rooms.map((r) => ({
        id: r.id,
        label: r.name,
        subtitle: r.description || undefined,
        to: `/inbox/${r.id}`,
      })),
    );
    push(
      'message',
      'Messages',
      'inbox',
      chat.messages.map((m) => ({
        id: m.id,
        label: m.text,
        subtitle: m.room_name,
        to: `/inbox/${m.room_id}`,
      })),
    );
  }

  return hits;
};
